// Bilingual reference data for 5 advanced techniques
import { Activity, BarChart3, Brain, Sparkles, Triangle, Layers } from "lucide-react";

export const ADVANCED_TECHNIQUES = [
  {
    id: "combination",
    icon: Layers,
    color: "from-pink-500 via-purple-500 to-cyan-500",
    name: { en: "Combination", ar: "التجميع" },
    summary: { en: "Unified signal from all 5 techniques", ar: "إشارة موحدة من كل التقنيات الخمس" },
    description: {
      en: "Aggregates signals from Chart Patterns + Wyckoff + SMC + Ichimoku + Harmonic Patterns. Highest probability when multiple techniques align.",
      ar: "يدمج إشارات أنماط الرسم البياني + ويكوف + SMC + إيشيموكو + الأنماط الهارمونيك. الاحتمالية الأعلى عندما تتوافق عدة تقنيات.",
    },
    rules: { en: ["Counts BUY signals vs SELL signals across all 5 techniques", "Confidence-weighted scoring", "Only enters when majority aligns"], ar: ["يحسب إشارات الشراء مقابل البيع عبر كل التقنيات الخمس", "تقييم مرجح بالثقة", "الدخول فقط عند توافق الأغلبية"] },
    entry: { en: ["Enter when 3+ techniques agree on direction", "Highest probability: 5/5 alignment"], ar: ["ادخل عند توافق 3+ تقنيات على الاتجاه", "أعلى احتمالية: توافق 5/5"] },
    exit: { en: ["Exit when techniques start to diverge", "Use weighted average for SL/TP"], ar: ["اخرج عند تباعد التقنيات", "استخدم متوسط مرجح لـ SL/TP"] },
    timeframes: ["H4", "Daily"],
  },
  {
    id: "chart-patterns",
    icon: Triangle,
    color: "from-blue-400 to-cyan-500",
    name: { en: "Chart Patterns", ar: "أنماط الرسم البياني" },
    summary: { en: "H&S, Double Top/Bottom, Triangles, Flags", ar: "الرأس والكتفين، القمم/القيعان المزدوجة، المثلثات، الأعلام" },
    description: {
      en: "Recurring price formations reflecting the battle between buyers and sellers. Signal future direction with measurable entry, stop, and targets.",
      ar: "تكوينات سعرية متكررة تعكس المعركة بين المشترين والبائعين. تشير لاتجاه السعر المستقبلي مع مستويات قابلة للقياس.",
    },
    rules: {
      en: [
        "Reversal patterns: Head & Shoulders, Double/Triple Top & Bottom",
        "Continuation patterns: Flags, Pennants, Triangles, Rectangles",
        "Volume confirmation required, especially for breakouts",
      ],
      ar: [
        "أنماط انعكاس: الرأس والكتفين، القمم/القيعان المزدوجة والثلاثية",
        "أنماط استمرار: الأعلام، الأقماع، المثلثات، المستطيلات",
        "تأكيد الحجم مطلوب، خاصة للاختراقات",
      ],
    },
    entry: {
      en: ["Reversal: Neckline retest after break", "Continuation: Breakout in trend direction", "Rectangles: Close outside range"],
      ar: ["انعكاس: إعادة اختبار خط العنق بعد الاختراق", "استمرار: اختراق في اتجاه الترند", "المستطيلات: إغلاق خارج النطاق"],
    },
    exit: {
      en: ["H&S: SL above right shoulder, target = head→neckline height", "Double Top: SL above peak", "Flags: target = flagpole length"],
      ar: ["رأس وكتفين: SL فوق الكتف الأيمن، الهدف = ارتفاع الرأس", "قمة مزدوجة: SL فوق القمة", "الأعلام: الهدف = طول السارية"],
    },
    timeframes: ["H4", "Daily"],
  },
  {
    id: "wyckoff",
    icon: BarChart3,
    color: "from-purple-400 to-violet-600",
    name: { en: "Wyckoff Method", ar: "طريقة ويكوف" },
    summary: { en: "Institutional accumulation & distribution", ar: "تجميع وتوزيع المؤسسات" },
    description: {
      en: "Markets are driven by large institutional operators (Composite Operator). Understanding their accumulation/distribution cycles gives retail traders an edge.",
      ar: "الأسواق مدفوعة من كبار المشغلين المؤسسيين (المشغل المركب). فهم دورات التجميع والتوزيع يمنح الأفراد ميزة.",
    },
    rules: {
      en: [
        "Law of Supply & Demand — price rises when demand exceeds supply",
        "Law of Cause & Effect — accumulation leads to uptrend",
        "Law of Effort vs Result — divergence between volume and price signals weakness",
      ],
      ar: [
        "قانون العرض والطلب — السعر يرتفع عندما يتجاوز الطلب العرض",
        "قانون السبب والنتيجة — التجميع يؤدي لاتجاه صاعد",
        "قانون الجهد مقابل النتيجة — الدايفرجنس بين الحجم والسعر يشير للضعف",
      ],
    },
    entry: {
      en: ["Accumulation: Buy at Last Point of Support (LPS) in Phase D", "Spring detected = high-probability long"],
      ar: ["تجميع: شراء عند نقطة الدعم الأخيرة (LPS) في المرحلة D", "اكتشاف الزنبرك = شراء عالي الاحتمال"],
    },
    exit: {
      en: ["SL below Spring low (accumulation) or above UTAD (distribution)", "Targets via markup phase"],
      ar: ["SL أسفل قاع الزنبرك (تجميع) أو فوق UTAD (توزيع)", "الأهداف عبر مرحلة الترقية"],
    },
    timeframes: ["Daily", "Weekly"],
  },
  {
    id: "smc",
    icon: Brain,
    color: "from-emerald-400 to-teal-600",
    name: { en: "Smart Money Concepts (ICT)", ar: "مفاهيم المال الذكي (ICT)" },
    summary: { en: "BOS, CHoCH, Order Blocks, Liquidity sweeps", ar: "اختراق الهيكل، تغيير الشخصية، كتل الأوامر، اصطياد السيولة" },
    description: {
      en: "Modern price-action methodology tracking institutional order flow. Focuses on how smart money manipulates retail traders before making their real moves.",
      ar: "منهجية سعرية حديثة تتبع تدفق أوامر المؤسسات. تركز على كيف يتلاعب المال الذكي بالأفراد قبل تحركاته الحقيقية.",
    },
    rules: {
      en: [
        "Market Structure: HH/HL (uptrend) vs LH/LL (downtrend)",
        "BOS (Break of Structure) = trend continuation",
        "CHoCH (Change of Character) = potential reversal",
        "Order Blocks = last opposite candle before strong move",
      ],
      ar: [
        "هيكلية السوق: HH/HL (صاعد) مقابل LH/LL (هابط)",
        "BOS (اختراق الهيكل) = استمرار الترند",
        "CHoCH (تغيير الشخصية) = انعكاس محتمل",
        "كتل الأوامر = آخر شمعة معاكسة قبل حركة قوية",
      ],
    },
    entry: {
      en: ["Define HTF bias → identify POI (OB/FVG) → drop to LTF → wait for CHoCH → enter on LTF OB"],
      ar: ["تحديد توجه HTF ← تحديد POI (OB/FVG) ← الانتقال لـ LTF ← انتظار CHoCH ← الدخول عند LTF OB"],
    },
    exit: {
      en: ["SL below entry OB (bullish) or above (bearish)", "TP at next liquidity level"],
      ar: ["SL أسفل كتلة الأوامر (شراء) أو فوقها (بيع)", "TP عند أقرب مستوى سيولة"],
    },
    timeframes: ["M15", "H1", "H4"],
  },
  {
    id: "ichimoku",
    icon: Sparkles,
    color: "from-rose-400 to-pink-600",
    name: { en: "Ichimoku Kinko Hyo", ar: "إيشيموكو كينكو هيو" },
    summary: { en: "Complete trading system: trend + momentum + S/R", ar: "نظام تداول كامل: اتجاه + زخم + دعم/مقاومة" },
    description: {
      en: "Provides trend direction, momentum, dynamic support/resistance, and trade signals in a single visual indicator.",
      ar: "يوفر اتجاه الترند، الزخم، الدعم/المقاومة الديناميكي، وإشارات التداول في مؤشر بصري واحد.",
    },
    rules: {
      en: [
        "Tenkan-sen (9): short-term trend",
        "Kijun-sen (26): medium-term trend, dynamic S/R",
        "Senkou A & B form the Kumo Cloud (future S/R)",
        "Chikou Span (lagging) confirms trend",
        "Price above cloud = bullish, below = bearish, inside = neutral",
      ],
      ar: [
        "تِنكان-سن (9): اتجاه قصير الأمد",
        "كيجون-سن (26): اتجاه متوسط، دعم/مقاومة ديناميكي",
        "سنكو A و B يشكلان سحابة كومو (دعم/مقاومة مستقبلية)",
        "تشيكو سبان (المتأخر) يؤكد الترند",
        "السعر فوق السحابة = صاعد، تحت = هابط، داخل = محايد",
      ],
    },
    entry: {
      en: ["Bullish: TK cross up + price above cloud + Chikou above price", "Bearish: TK cross down + price below cloud + Chikou below"],
      ar: ["صاعد: تقاطع TK لأعلى + السعر فوق السحابة + تشيكو فوق السعر", "هابط: تقاطع TK لأسفل + السعر تحت السحابة + تشيكو تحت السعر"],
    },
    exit: {
      en: ["Kijun-sen as dynamic SL for swing trades", "TP via projected S/R levels"],
      ar: ["كيجون-سن كـ SL ديناميكي لصفقات السوينج", "TP عبر مستويات الدعم/المقاومة المتوقعة"],
    },
    timeframes: ["H4", "Daily"],
  },
  {
    id: "harmonic",
    icon: Activity,
    color: "from-amber-400 to-orange-600",
    name: { en: "Harmonic Patterns", ar: "الأنماط الهارمونيك" },
    summary: { en: "Gartley, Bat, Butterfly, Crab — Fibonacci-based reversals", ar: "جارتلي، بات، فراشة، سلطعون — انعكاسات مبنية على فيبوناتشي" },
    description: {
      en: "Use Fibonacci ratios to identify precise Potential Reversal Zones (PRZ). 5-point structures (X-A-B-C-D) where each leg conforms to specific Fib ratios.",
      ar: "تستخدم نسب فيبوناتشي لتحديد مناطق انعكاس دقيقة (PRZ). هياكل من 5 نقاط (X-A-B-C-D) حيث كل ساق يلتزم بنسب فيبوناتشي محددة.",
    },
    rules: {
      en: [
        "Gartley: AB=0.618 XA, CD=1.272-1.618 BC",
        "Bat: AB=0.382-0.500 XA, CD=1.618-2.618 BC",
        "Butterfly: AB=0.786 XA, CD=1.618-2.618 BC",
        "Crab: AB=0.382-0.618 XA, CD=2.618-3.618 BC",
        "Point D is the Potential Reversal Zone (PRZ)",
      ],
      ar: [
        "جارتلي: AB=0.618 XA، CD=1.272-1.618 BC",
        "بات: AB=0.382-0.500 XA، CD=1.618-2.618 BC",
        "فراشة: AB=0.786 XA، CD=1.618-2.618 BC",
        "سلطعون: AB=0.382-0.618 XA، CD=2.618-3.618 BC",
        "النقطة D هي منطقة الانعكاس المحتملة (PRZ)",
      ],
    },
    entry: {
      en: ["Price reaches PRZ at point D", "Wait for reversal confirmation (candle pattern, RSI divergence)"],
      ar: ["وصول السعر لـ PRZ عند النقطة D", "انتظار تأكيد الانعكاس (نمط شمعي، دايفرجنس RSI)"],
    },
    exit: {
      en: ["SL beyond X point", "T1 = 38.2% of CD, T2 = 61.8% of CD, T3 = point A"],
      ar: ["SL وراء النقطة X", "T1 = 38.2% من CD، T2 = 61.8% من CD، T3 = النقطة A"],
    },
    timeframes: ["H4", "Daily"],
  },
];
